/**
 * Guide Card Component
 *
 * Card component for single guide item
 */

import { Card, Badge, Text, Title, Button, Group, Stack } from '@mantine/core';
import { IconArrowRight } from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import type { GuideItem } from '../../../../data/guide/guideTypes';

interface GuideCardProps {
  item: GuideItem;
  featured?: boolean;
  showCTA?: boolean;
}

/**
 * Guide Card Component
 *
 * Displays a guide item as a card with category, date, title, and excerpt.
 * Featured cards have gradient background and "Wyróżnione" badge.
 */
export function GuideCard({ item, featured = false, showCTA = true }: GuideCardProps) {
  const formattedDate = new Date(item.date).toLocaleDateString('pl-PL', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    <Card
      shadow="sm"
      padding="lg"
      radius="md"
      withBorder
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'transform var(--ec-transition-normal, 200ms ease), box-shadow var(--ec-transition-normal, 200ms ease)',
        background: featured
          ? 'linear-gradient(135deg, var(--mantine-color-ecoBlue-0) 0%, var(--mantine-color-ecoBlue-1) 100%)'
          : undefined,
      }}
    >
      <Stack gap="md" style={{ flex: 1 }}>
        {/* Header: Category badge + featured badge */}
        <Group justify="space-between" align="center">
          <Badge color="blue" variant="light" size="md">
            Analiza
          </Badge>
          {featured && (
            <Badge color="blue" variant="filled" size="sm">
              Wyróżnione
            </Badge>
          )}
        </Group>

        <Text size="sm" c="dimmed">
          {formattedDate} • {item.readTime}
        </Text>

        {/* Title */}
        <Title order={3} size="h4" lh={1.3}>
          {item.title}
        </Title>

        {/* Excerpt */}
        <Text size="sm" c="dimmed" style={{ flex: 1 }}>
          {item.excerpt}
        </Text>

        {/* Tags */}
        <Group gap="xs">
          {item.tags.slice(0, 3).map((tag) => (
            <Badge key={tag} color="gray" variant="light" size="sm">
              {tag}
            </Badge>
          ))}
        </Group>

        {/* CTA Button */}
        {showCTA && (
          <Button
            component={Link}
            to={`/przewodnik/analizy/${item.slug}`}
            variant={featured ? 'filled' : 'light'}
            color="blue"
            rightSection={<IconArrowRight size={16} />}
            fullWidth
            mt="auto"
          >
            Czytaj analizę
          </Button>
        )}
      </Stack>
    </Card>
  );
}
